'use client';
import React, { useState } from 'react';
import { Search, MapPin, School, Film } from 'lucide-react';
import Link from 'next/link';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const mockResults = [
  { id: 'res-001', production: 'Hamilton', school: 'Westside High School', city: 'Austin, TX', views: 3240 },
  { id: 'res-002', production: 'Les Misérables', school: 'Northbrook Academy', city: 'Chicago, IL', views: 2180 },
  { id: 'res-003', production: 'Grease', school: 'Riverside Theatre Co.', city: 'Nashville, TN', views: 1890 },
  { id: 'res-004', production: 'The Phantom of the Opera', school: 'Summit Arts Center', city: 'Denver, CO', views: 4120 },
  { id: 'res-005', production: 'Chicago', school: 'Lakewood Performing Arts', city: 'Seattle, WA', views: 2750 },
];

type SearchFilter = 'all' | 'production' | 'school' | 'city';

export default function SearchSection() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SearchFilter>('all');
  const [results, setResults] = useState<typeof mockResults>([]);
  const [searched, setSearched] = useState(false);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: searchRef, isVisible: searchVisible } = useScrollReveal({ threshold: 0.1 });

  const handleSearch = () => {
    if (!query.trim()) return;
    const filtered = mockResults.filter((r) => {
      const q = query.toLowerCase();
      if (filter === 'production') return r.production.toLowerCase().includes(q);
      if (filter === 'school') return r.school.toLowerCase().includes(q);
      if (filter === 'city') return r.city.toLowerCase().includes(q);
      return r.production.toLowerCase().includes(q) || r.school.toLowerCase().includes(q) || r.city.toLowerCase().includes(q);
    });
    setResults(filtered);
    setSearched(true);
  };

  const filters: { id: SearchFilter; label: string; icon: React.ElementType }[] = [
    { id: 'all', label: 'All', icon: Search },
    { id: 'production', label: 'Production', icon: Film },
    { id: 'school', label: 'School', icon: School },
    { id: 'city', label: 'City', icon: MapPin },
  ];

  return (
    <section id="search" className="py-16 relative" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <div
            ref={headerRef}
            className={`text-center mb-8 ${headerVisible ? 'reveal-up delay-0' : 'reveal-hidden'}`}
          >
            <h2 className="text-3xl font-extrabold text-zinc-100 mb-2">Find a Program</h2>
            <p className="text-zinc-400">Search by production name, school, or city</p>
          </div>

          {/* Search bar */}
          <div
            ref={searchRef}
            className={`${searchVisible ? 'reveal-up delay-100' : 'reveal-hidden'}`}
          >
            <div className="flex gap-2 mb-4 p-2" style={{ backgroundColor: '#1e2235', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px' }}>
              <div className="flex-1 flex items-center gap-3 px-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}>
                <Search size={18} className="text-zinc-500 shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search productions, schools, cities..."
                  className="flex-1 bg-transparent text-zinc-100 placeholder-zinc-500 text-sm py-3 outline-none"
                />
              </div>
              <button
                onClick={handleSearch}
                className="btn-gold font-bold px-6 py-3 text-sm shrink-0"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)', color: '#0f0f1a', borderRadius: '4px' }}
              >
                Search
              </button>
            </div>

            {/* Filter chips */}
            <div className="flex items-center gap-2 mb-8">
              {filters.map((f) => {
                const FilterIcon = f.icon;
                return (
                  <button
                    key={`filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={`btn-ghost flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-all duration-150 ${
                      filter === f.id ? 'text-zinc-950' : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    style={
                      filter === f.id
                        ? { background: 'linear-gradient(135deg, #f59e0b, #f97316)', borderRadius: '3px' }
                        : { backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px' }
                    }
                  >
                    <FilterIcon size={12} />
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results */}
          {searched && (
            <div className="animate-fade-in">
              {results.length === 0 ? (
                <div className="text-center py-12">
                  <Film size={40} className="text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-400 font-medium">No programs found for &ldquo;{query}&rdquo;</p>
                  <p className="text-zinc-600 text-sm mt-1">Try a different search term or browse popular programs below</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {results.map((r) => (
                    <Link
                      key={r.id}
                      href="/program-viewer"
                      className="flex items-center justify-between px-5 py-4 transition-all duration-150 group hover:border-gold-500/40"
                      style={{ backgroundColor: '#1e2235', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px' }}
                    >
                      <div>
                        <div className="font-bold text-zinc-100 group-hover:text-gold-400 transition-colors">{r.production}</div>
                        <div className="text-sm text-zinc-500 mt-0.5">{r.school} · {r.city}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-zinc-300 tabular-nums">{r.views.toLocaleString()}</div>
                        <div className="text-xs text-zinc-600">views</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}